import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import moment from 'moment';
import * as React from 'react';
import { useMemo } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AttachmentInput } from '~/components/AttachmentInput';
import { AbsenceType } from '~/data/absences/types';
import { Enum } from '~/data/enums';
import {
  ShortAbsenceDurationFields,
  ShortAbsenceDurationFieldsProps,
} from '~/features/AbsenceChanges/EditAbsenceDialog/components/AbsenceFormFields/ShortAbsenceDurationFields';
import { SelectedAbsenceBalance } from '~/features/AbsenceChanges/EditAbsenceDialog/components/AbsenceFormFields/ShortAbsenceDurationFields/SelectedAbsenceBalance';
import { AbsenceForm } from '~/features/AbsenceChanges/EditAbsenceDialog/data';
import {
  AbsenceReasonFieldValue,
  YearFieldValue,
} from '~/features/AbsenceChanges/EditAbsenceDialog/useAbsenceForm';
import { BalancePool } from '~/scenes/MyWorkspace/components/Widgets/components/MyDataWidget/components/Content/useVacationBalances';
import { theme } from '~/theme';
import {
  convertDateTypeDatePickerProps,
  getEmployeeAttachmentUrl,
} from '~/utils/helper';

import { useSxStyles } from './useStyles';

export type AbsenceFormFieldsProps = {
  employeeId: number;
  absenceType: AbsenceType;
  formMethods: UseFormReturn<AbsenceForm>;
  fieldOptions: {
    absenceReasons: Enum[];
    yearOptions: Enum[];
  };
  absencesSettings: {
    startDate: Date | null;
  } & ShortAbsenceDurationFieldsProps;
  absenceBalances: {
    availableBalance: BalancePool | undefined;
    getBalanceForAbsenceReason: (
      fieldReason: AbsenceReasonFieldValue,
    ) => BalancePool | undefined;
    getBalanceForYear: (year: YearFieldValue) => BalancePool | undefined;
  };
};

export const AbsenceFormFields = (props: AbsenceFormFieldsProps) => {
  const {
    employeeId,
    absenceType,
    formMethods,
    absencesSettings,
    fieldOptions: { absenceReasons, yearOptions },
    absenceBalances: {
      availableBalance,
      getBalanceForAbsenceReason,
      getBalanceForYear,
    },
  } = props;
  const { startDate, ...shortAbsenceDurationProps } = absencesSettings;

  const { control, setValue } = formMethods;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const attachmentId = useWatch({ name: 'attachmentId', control });
  const attachmentUrl = useMemo(() => {
    return !!attachmentId && attachmentId > 0
      ? getEmployeeAttachmentUrl(employeeId, attachmentId)
      : undefined;
  }, [employeeId, attachmentId]);

  const OptionWithBalance = (optionProps: {
    value: React.ReactNode;
    balance?: BalancePool;
  }) => {
    const { value, balance } = optionProps;

    return (
      <Box>
        <Typography component={'span'} sx={{ display: 'block' }}>
          {value}
        </Typography>
        {typeof balance?.balance !== 'undefined' && (
          <Typography variant={'caption'} color={theme.palette.text.secondary}>
            {`${t('laBalance')}: ${t('NDays', { days: balance.balance })}`}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <>
      <Controller
        control={control}
        name={'absenceReason'}
        render={({
          field: { onChange, value, ref, ...field },
          fieldState: { error },
        }) => (
          <Autocomplete
            options={absenceReasons}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option: Enum, value) =>
              option.code === value.code
            }
            value={value || null}
            onChange={(_, value) => onChange(value || null)}
            renderOption={(props, option) => {
              return (
                <li {...props}>
                  <OptionWithBalance
                    value={option.name}
                    balance={getBalanceForAbsenceReason(option)}
                  />
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                {...field}
                label={t('absenceReason')}
                required
                inputRef={ref}
                margin="normal"
                error={!!error}
                helperText={error?.message}
                data-testid="absenceForm-dropdown-reason"
              />
            )}
          />
        )}
      />
      {yearOptions.length > 0 && (
        <Controller
          control={control}
          name={'year'}
          render={({
            field: { onChange, value, ref, ...field },
            fieldState: { error },
          }) => (
            <Autocomplete
              options={yearOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option: Enum, value) =>
                option.code === value.code
              }
              value={value || null}
              onChange={(_, value) => onChange(value || null)}
              renderOption={(props, option) => {
                return (
                  <li {...props}>
                    <OptionWithBalance
                      value={option.name}
                      balance={getBalanceForYear(option)}
                    />
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...field}
                  label={t('laYear')}
                  required
                  inputRef={ref}
                  margin="normal"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          )}
        />
      )}
      {availableBalance && (
        <SelectedAbsenceBalance balancePool={availableBalance} />
      )}
      {absenceType === AbsenceType.SHORT && (
        <ShortAbsenceDurationFields
          formMethods={formMethods}
          {...shortAbsenceDurationProps}
        />
      )}
      {absenceType === AbsenceType.LONG && (
        <Box sx={sxStyles.formContentRow}>
          <Controller
            control={control}
            name={'startDate'}
            render={({
              field: { name, value, onChange, ref },
              fieldState: { error },
            }) => (
              <DatePicker
                format="DD.MM.YYYY"
                {...convertDateTypeDatePickerProps({
                  value,
                  onChange,
                })}
                inputRef={ref}
                minDate={moment().subtract(366, 'days')}
                slotProps={{
                  textField: {
                    name,
                    label: t('startDate'),
                    required: true,
                    fullWidth: true,
                    margin: 'normal',
                    error: !!error,
                    helperText: error?.message,
                  },
                }}
              />
            )}
          />
          <Box sx={sxStyles.rangeDivider}>-</Box>
          <Controller
            control={control}
            name={'endDate'}
            render={({
              field: { name, value, onChange, ref },
              fieldState: { error },
            }) => (
              <DatePicker
                format="DD.MM.YYYY"
                {...convertDateTypeDatePickerProps({
                  value,
                  onChange,
                })}
                inputRef={ref}
                minDate={moment(startDate)}
                slotProps={{
                  textField: {
                    name,
                    label: t('endDate'),
                    fullWidth: true,
                    margin: 'normal',
                    error: !!error,
                    helperText: error?.message,
                  },
                }}
              />
            )}
          />
        </Box>
      )}
      <Controller
        control={control}
        name={'info'}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            type="text"
            label={t('info')}
            multiline={true}
            fullWidth={true}
            margin="normal"
            error={!!error}
            helperText={error?.message}
            data-cy="info"
          />
        )}
      />

      <Controller
        name={'attachmentName'}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <AttachmentInput
              onChange={(selectedFile?: File) => {
                onChange(selectedFile?.name || '');
                setValue('attachment', selectedFile || null);
                setValue('attachmentId', -1);
              }}
              value={value}
              attachmentUrl={attachmentUrl}
              fullWidth={true}
              margin={'normal'}
              label={t('attachment')}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
    </>
  );
};
